"use client";

// The Work OS shell with the DTP look (design spec §1.1, §2, §8.1): a 56 px rail of modes, the
// mode's 256 px sidebar (resizable 224–360, `[` collapses), a 56 px top bar, the scrolling main
// region and an optional 360 px side panel. Apps pass the regions as slots; every recipe lives
// here.
//
// Narrow screens (§2 Mobile): below 1024 px the sidebar leaves the grid and slides in over the
// page from the hamburger (`data-nav="open"`); below 768 px the rail hides and the bottom bar
// carries the modes.
import { MenuIcon, PanelLeftCloseIcon, PanelLeftOpenIcon } from "lucide-react";
import * as React from "react";
import { cn } from "../cn";
import { Button } from "./button";
import { Kbd } from "./kbd";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

const SIDEBAR_MIN = 224;
const SIDEBAR_MAX = 360;
const SIDEBAR_DEFAULT = 256;
const SIDEBAR_STEP = 16;
const COLLAPSED_KEY = "companyos.sidebar.collapsed";
const WIDTH_KEY = "companyos.sidebar.width";
const NARROW = "(max-width: 1023.98px)";

const clampWidth = (px: number) => Math.round(Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, px)));

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // storage blocked: the choice holds for this page
  }
}

function readWidth(): number {
  const n = Number.parseInt(read(WIDTH_KEY) ?? "", 10);
  return Number.isFinite(n) ? clampWidth(n) : SIDEBAR_DEFAULT;
}

function isNarrow(): boolean {
  return typeof window !== "undefined" && (window.matchMedia?.(NARROW).matches ?? false);
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName);
}

/** A modal, menu or listbox owns the keyboard while it is open. */
function overlayOpen(): boolean {
  return (
    document.querySelector(
      '[role="dialog"][aria-modal="true"], [role="alertdialog"], [role="menu"], [role="listbox"]',
    ) !== null
  );
}

export type ShellLayout = {
  hasSidebar: boolean;
  sidebarCollapsed: boolean;
  /** Collapse (true), expand (false) or flip (no argument). Persisted. */
  toggleSidebar: (collapse?: boolean) => void;
  sidebarWidth: number;
  /** Clamped to 224–360; persisted unless `persist` is false (live drag). */
  setSidebarWidth: (px: number, persist?: boolean) => void;
  navOpen: boolean;
  setNavOpen: (open: boolean) => void;
};

const INERT: ShellLayout = {
  hasSidebar: false,
  sidebarCollapsed: false,
  toggleSidebar: () => {},
  sidebarWidth: SIDEBAR_DEFAULT,
  setSidebarWidth: () => {},
  navOpen: false,
  setNavOpen: () => {},
};

const ShellLayoutContext = React.createContext<ShellLayout>(INERT);

/** The shell's layout state; inert outside an `AppShell` (so parts render on their own). */
function useShellLayout(): ShellLayout {
  return React.useContext(ShellLayoutContext);
}

function SkipLink() {
  return (
    <a
      href="#main"
      className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-tooltip focus:rounded-md focus:bg-card focus:px-3 focus:py-2 focus:text-sm focus:shadow-md focus:outline-2 focus:outline-ring"
    >
      Skip to content
    </a>
  );
}

type AppShellProps = {
  /** The mode rail (`Rail`); absent on the company picker. */
  rail?: React.ReactNode;
  /** The mode's sidebar panel (`SidebarPanel`); null when the mode has none. */
  sidebar?: React.ReactNode;
  /** Whose sidebar this is (the mode): its collapsed state is remembered per key. */
  sidebarKey?: string;
  topbar: React.ReactNode;
  /** The side panel (`SidePanel`), rendered only while open. */
  panel?: React.ReactNode;
  /** Phones: the bottom mode bar (`BottomBar`). */
  bottomBar?: React.ReactNode;
  /** `doc`: padded, max 1440 px (document pages); `full`: edge to edge (tables, boards). */
  main?: "doc" | "full";
  children: React.ReactNode;
  className?: string;
};

function AppShell({
  rail,
  sidebar,
  sidebarKey,
  topbar,
  panel,
  bottomBar,
  main = "doc",
  children,
  className,
}: AppShellProps) {
  const hasSidebar = sidebar !== null && sidebar !== undefined && sidebar !== false;
  // SIDEBAR-TOGGLE-FIXED: each mode remembers its own choice; a mode that has none yet takes
  // the shell-wide one (what every mode shared before).
  const collapsedKey = sidebarKey ? `${COLLAPSED_KEY}.${sidebarKey}` : COLLAPSED_KEY;
  const [chosen, setChosen] = React.useState<Record<string, boolean>>({});
  const collapsed = chosen[collapsedKey] ?? (read(collapsedKey) ?? read(COLLAPSED_KEY)) === "1";
  const [width, setWidth] = React.useState(readWidth);
  const [navOpen, setNavOpen] = React.useState(false);
  // The top bar is 56 px, plus the chip row when it does not fit on one line: drawers and the
  // phone side panel start under its real height (`--shell-top`).
  const topbarRef = React.useRef<HTMLDivElement>(null);
  const [topHeight, setTopHeight] = React.useState<number | null>(null);
  React.useEffect(() => {
    const el = topbarRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setTopHeight(Math.round(el.offsetHeight)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const toggleSidebar = React.useCallback(
    (collapse?: boolean) => {
      const next = collapse ?? !collapsed;
      write(collapsedKey, next ? "1" : "0");
      setChosen((prev) => ({ ...prev, [collapsedKey]: next }));
    },
    [collapsed, collapsedKey],
  );

  const setSidebarWidth = React.useCallback((px: number, persist = true) => {
    const next = clampWidth(px);
    setWidth(next);
    if (persist) write(WIDTH_KEY, String(next));
  }, []);

  // `[` collapses the sidebar (or toggles the nav drawer on narrow screens); never while typing,
  // with a modifier, or while a modal or menu holds the keyboard.
  React.useEffect(() => {
    if (!hasSidebar) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "[" || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.defaultPrevented || isTyping(event.target) || overlayOpen()) return;
      event.preventDefault();
      if (isNarrow()) setNavOpen((open) => !open);
      else toggleSidebar();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [hasSidebar, toggleSidebar]);

  // Escape closes the nav drawer, after any menu or dialog has had its turn (§2.1).
  React.useEffect(() => {
    if (!navOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented && !overlayOpen()) setNavOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    const mql = window.matchMedia?.(NARROW);
    const onWide = () => {
      if (!mql?.matches) setNavOpen(false);
    };
    mql?.addEventListener("change", onWide);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      mql?.removeEventListener("change", onWide);
    };
  }, [navOpen]);

  const value = React.useMemo<ShellLayout>(
    () => ({
      hasSidebar,
      sidebarCollapsed: collapsed,
      toggleSidebar,
      sidebarWidth: width,
      setSidebarWidth,
      navOpen,
      setNavOpen,
    }),
    [hasSidebar, collapsed, toggleSidebar, width, setSidebarWidth, navOpen],
  );

  return (
    <ShellLayoutContext.Provider value={value}>
      <div
        data-slot="app-shell"
        data-sidebar={collapsed ? "collapsed" : "expanded"}
        data-nav={navOpen ? "open" : "closed"}
        // The live sidebar width (the token holds the default): the one runtime variable.
        data-bottom-bar={bottomBar ? "" : undefined}
        style={
          {
            "--sidebar-w": `${width}px`,
            ...(topHeight ? { "--shell-top": `${topHeight}px` } : {}),
          } as React.CSSProperties
        }
        className={cn(
          "group/shell grid h-dvh grid-cols-[auto_auto_minmax(0,1fr)_auto] grid-rows-[auto_minmax(0,1fr)] overflow-hidden bg-background text-foreground",
          className,
        )}
      >
        <SkipLink />
        {rail ? (
          <div className="relative z-[26] col-start-1 row-span-2 row-start-1 flex min-h-0 max-md:hidden">
            {rail}
          </div>
        ) : null}
        <div ref={topbarRef} className="z-topbar col-start-2 col-end-[-1] row-start-1 min-w-0">
          {topbar}
        </div>
        {hasSidebar ? <ShellSidebar>{sidebar}</ShellSidebar> : null}
        {hasSidebar && navOpen ? (
          <div
            aria-hidden="true"
            data-slot="nav-scrim"
            className="fixed inset-x-0 top-(--shell-top,var(--topbar-h)) bottom-0 z-[24] bg-overlay animate-in fade-in-0 duration-(--dur-slow) lg:hidden"
            onClick={() => setNavOpen(false)}
          />
        ) : null}
        <main
          id="main"
          tabIndex={-1}
          className={cn(
            "col-start-3 row-start-2 min-h-0 min-w-0 overflow-y-auto scrollbar-gutter-stable outline-none",
            bottomBar ? "max-md:pb-24" : "",
          )}
        >
          {main === "doc" ? (
            <div
              data-slot="page-frame"
              className="mx-auto w-full max-w-page px-8 pt-7 pb-12 max-md:px-4 max-md:pt-5 max-md:pb-10"
            >
              {children}
            </div>
          ) : (
            children
          )}
        </main>
        {panel ? <div className="col-start-4 row-start-2 flex min-h-0">{panel}</div> : null}
        {bottomBar}
      </div>
    </ShellLayoutContext.Provider>
  );
}

/** The sidebar column: resizable by drag or ArrowLeft/Right on its edge (§2 Sidebar). */
function ShellSidebar({ children, className }: { children: React.ReactNode; className?: string }) {
  const { sidebarWidth, setSidebarWidth, setNavOpen } = useShellLayout();
  const drag = React.useRef<{ x: number; w: number } | null>(null);

  return (
    <aside
      id="shell-sidebar"
      aria-label="Sidebar"
      data-slot="sidebar"
      // Following a link in the drawer closes it; the rail stays open to pick a place.
      onClickCapture={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest("a[href]") && isNarrow()) setNavOpen(false);
      }}
      className={cn(
        "relative flex w-sidebar min-h-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground",
        // SIDEBAR-TOGGLE-FIXED: collapsed, it narrows to the rail-wide strip (`SidebarPanel`'s
        // icons), never disappears: its toggle stays where it was.
        "lg:z-sidebar lg:col-start-2 lg:row-start-2 lg:group-data-[sidebar=collapsed]/shell:w-rail",
        // Narrow: an off-canvas drawer under the top bar (beside the rail from 768 px).
        "max-lg:invisible max-lg:fixed max-lg:top-(--shell-top,var(--topbar-h)) max-lg:bottom-0 max-lg:left-rail max-lg:z-[25] max-lg:-translate-x-full max-lg:shadow-lg max-lg:transition-[translate,visibility] max-lg:duration-(--dur-slow) max-lg:ease-out max-md:left-0 max-md:w-72 max-md:group-data-bottom-bar/shell:bottom-[calc(3.5rem+env(safe-area-inset-bottom))]",
        "max-lg:group-data-[nav=open]/shell:visible max-lg:group-data-[nav=open]/shell:translate-x-0",
        className,
      )}
    >
      {children}
      {/* biome-ignore lint/a11y/useSemanticElements: an <hr> cannot take focus or a value; the
          WAI-ARIA window splitter is a focusable separator. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        aria-valuemin={SIDEBAR_MIN}
        aria-valuemax={SIDEBAR_MAX}
        aria-valuenow={sidebarWidth}
        tabIndex={0}
        data-slot="sidebar-resize"
        onPointerDown={(event) => {
          event.preventDefault();
          (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
          drag.current = { x: event.clientX, w: sidebarWidth };
        }}
        onPointerMove={(event) => {
          if (drag.current) setSidebarWidth(drag.current.w + event.clientX - drag.current.x, false);
        }}
        onPointerUp={(event) => {
          if (!drag.current) return;
          setSidebarWidth(drag.current.w + event.clientX - drag.current.x, true);
          drag.current = null;
        }}
        onDoubleClick={() => setSidebarWidth(SIDEBAR_DEFAULT)}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.preventDefault();
            setSidebarWidth(
              sidebarWidth + (event.key === "ArrowRight" ? SIDEBAR_STEP : -SIDEBAR_STEP),
            );
          } else if (event.key === "Home" || event.key === "End") {
            event.preventDefault();
            setSidebarWidth(event.key === "Home" ? SIDEBAR_MIN : SIDEBAR_MAX);
          }
        }}
        className="absolute top-0 -right-0.5 z-10 h-full w-1 cursor-col-resize touch-none outline-none transition-colors duration-(--dur-fast) hover:bg-primary/40 focus-visible:bg-primary active:bg-primary max-lg:hidden group-data-[sidebar=collapsed]/shell:hidden"
      />
    </aside>
  );
}

/** Opens and closes the nav drawer; below 1024 px only, and only when there is a sidebar. */
function NavToggleButton({ className }: { className?: string }) {
  const { hasSidebar, navOpen, setNavOpen } = useShellLayout();
  if (!hasSidebar) return null;
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={navOpen ? "Close navigation" : "Open navigation"}
      aria-expanded={navOpen}
      aria-controls="shell-sidebar"
      data-slot="nav-toggle-button"
      className={cn("pointer-coarse:size-11 lg:hidden", className)}
      onClick={() => setNavOpen(!navOpen)}
    >
      <MenuIcon aria-hidden="true" />
    </Button>
  );
}

/**
 * A top-bar reopen button for shells whose sidebar has no strip. The company shell no longer
 * uses it (SIDEBAR-TOGGLE-FIXED: the toggle stays on the sidebar's strip).
 */
function SidebarReopenButton({ className }: { className?: string }) {
  const { hasSidebar, sidebarCollapsed, toggleSidebar } = useShellLayout();
  if (!hasSidebar || !sidebarCollapsed) return null;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            aria-label="Show sidebar"
            aria-keyshortcuts="["
            data-slot="sidebar-reopen"
            className={cn("max-lg:hidden", className)}
            onClick={() => toggleSidebar(false)}
          />
        }
      >
        <PanelLeftOpenIcon aria-hidden="true" />
      </TooltipTrigger>
      <TooltipContent side="bottom">
        Show sidebar <Kbd>[</Kbd>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * SIDEBAR-TOGGLE-FIXED: the sidebar's one toggle, the first thing at the top-left of the sidebar
 * in both states (the panel header open, the strip collapsed: same place). `hide` collapses on
 * wide screens and closes the drawer otherwise; `show` brings the panel back.
 */
function SidebarToggleButton({
  action,
  className,
}: {
  action: "hide" | "show";
  className?: string;
}) {
  const { toggleSidebar, setNavOpen } = useShellLayout();
  const hide = action === "hide";
  const label = hide ? "Hide sidebar" : "Show sidebar";
  const Icon = hide ? PanelLeftCloseIcon : PanelLeftOpenIcon;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            aria-keyshortcuts="["
            aria-controls="shell-sidebar"
            data-slot="sidebar-toggle"
            className={cn(
              "shrink-0 text-muted-foreground hover:text-foreground pointer-coarse:size-11",
              className,
            )}
            onClick={() =>
              hide ? (isNarrow() ? setNavOpen(false) : toggleSidebar(true)) : toggleSidebar(false)
            }
          />
        }
      >
        <Icon aria-hidden="true" />
      </TooltipTrigger>
      <TooltipContent side={hide ? "bottom" : "right"}>
        {label} <Kbd>[</Kbd>
      </TooltipContent>
    </Tooltip>
  );
}

/** The panel header's toggle (open state); kept for callers of the old name. */
function SidebarCollapseButton({ className }: { className?: string }) {
  return <SidebarToggleButton action="hide" {...(className ? { className } : {})} />;
}

export {
  AppShell,
  NavToggleButton,
  ShellSidebar,
  SidebarCollapseButton,
  SidebarReopenButton,
  SidebarToggleButton,
  useShellLayout,
};
